import { Ev } from '../events';
import type { World } from '../World';

/**
 * La machine de PHASES de combat — la notion de boss dans le moteur (PLAN.md §6.4).
 *
 * Un ennemi ordinaire a un comportement immuable ; un boss ÉVOLUE. Ce système
 * fait avancer la phase de chaque ennemi porteur de `phases` quand sa fraction de
 * PV franchit un seuil décroissant, re-télégraphie la bascule (le boss se fige,
 * clignote, devient brièvement intouchable — « ça change ») et la SIGNALE
 * (`Ev.BossPhase`) pour la mise en scène. `emitterSystem` lit alors l'émetteur de
 * la nouvelle phase au lieu de `def.emitter`.
 *
 * ⚠️ Tourne APRÈS `damageSystem` : les PV du tick sont finaux, et la phase mise à
 * jour ici prend effet au tick SUIVANT (latence d'un tick, imperceptible — le
 * même motif que la chauffe du canon). Aucun RNG : transitions purement
 * déterministes, sur comparaison de PV.
 *
 * Coût nul pour tout ennemi sans `phases` (l'écrasante majorité) : la boucle les
 * saute d'emblée.
 */
export function phaseSystem(w: World): void {
  const en = w.enemies;
  for (let i = 0; i < en.count; i++) {
    const s = en.at(i);
    const def = w.defs.enemies[en.kind[s]!];
    const phases = def?.phases;
    if (!phases || phases.length === 0) continue;

    const maxHp = en.maxHp[s]!;
    if (maxHp <= 0) continue;
    const frac = en.hp[s]! / maxHp;
    // Parties DÉTRUITES = total déclaré − parties encore vivantes. Deux axes de
    // déclenchement, tous deux MONOTONES (les PV ne remontent pas, une partie
    // morte ne renaît pas) : une phase activée le reste.
    const totalParts = def!.parts?.length ?? 0;
    const partsLost = totalParts - en.livingParts(s);

    /* Phase CIBLE = la PLUS AVANCÉE activée. Une phase est activée si son seuil de
       PV OU son seuil de parties est franchi. On ne coupe pas la boucle : les deux
       axes peuvent s'activer dans le désordre (démonter avant d'entamer les PV), et
       c'est l'auteur du contenu qui ordonne les phases du plus tôt au plus tard.
       La monotonie des deux axes garantit qu'aucune phase activée ne se désactive. */
    let target = 0;
    for (let k = 0; k < phases.length; k++) {
      const ph = phases[k]!;
      const hpTrig = ph.atHpFrac !== undefined && frac < ph.atHpFrac;
      const partTrig = ph.atPartsLost !== undefined && partsLost >= ph.atPartsLost;
      if (hpTrig || partTrig) target = k + 1;
    }

    // Les déclencheurs sont monotones : une phase ne recule jamais. On ne bascule
    // que vers l'avant, une transition à la fois consommée d'un coup.
    if (target <= en.phase[s]!) continue;

    en.phase[s] = target;
    const ph = phases[target - 1]!;
    const tel = ph.telegraph ?? 0;
    if (tel > 0) {
      // Le boss se fige et devient intouchable le temps du télégraphe ; le premier
      // tir de la nouvelle attaque tombe pile à la fin de celui-ci.
      en.telegraph[s] = tel;
      en.fireCd[s] = tel;
    }
    // Invocation propre à la phase : on repart d'une ponte NEUVE (compteurs à zéro),
    // pour que chaque phase invoque à son propre rythme et son propre plafond.
    if (ph.brood) {
      en.broodCd[s] = 0;
      en.broodDone[s] = 0;
    }
    w.events.push(Ev.BossPhase, target, en.x[s]!, en.y[s]!, 0);
  }
}
